// "Sign in with Apple JS" integration for the admin back-office.
//
// The back-office is a plain web app, so we load Apple's JS SDK and use the
// popup flow. The popup MUST be opened from a direct user gesture (a click
// handler), otherwise the browser blocks it — so the login button should call
// `signInWithApple()` straight from its onClick, not via an async/redux
// roundtrip.

const APPLE_SDK_URL =
	"https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";

// The Services ID registered in the Apple Developer portal (shared by all
// DigiHapi web surfaces). Overridable via env if ever needed.
const APPLE_WEB_SERVICE_ID =
	process.env.REACT_APP_APPLE_SERVICE_ID || "com.digihapi";

let sdkPromise = null;
let initialized = false;

// The redirect URI must (a) be on the admin's own origin so the popup can relay
// the result via postMessage, and (b) be registered as a Return URL for the
// Services ID in Apple. Deriving it from the current origin makes it follow the
// environment (staging vs production) automatically.
const getRedirectUri = () =>
	typeof window !== "undefined" ? `${window.location.origin}/` : "";

// Load the AppleID JS SDK once (no-op if already present).
const loadAppleSdk = () => {
	if (typeof window !== "undefined" && window.AppleID) {
		return Promise.resolve();
	}
	if (sdkPromise) {
		return sdkPromise;
	}
	sdkPromise = new Promise((resolve, reject) => {
		const script = document.createElement("script");
		script.src = APPLE_SDK_URL;
		script.async = true;
		script.onload = () => resolve();
		script.onerror = () =>
			reject(new Error("Failed to load the Sign in with Apple SDK"));
		document.head.appendChild(script);
	});
	return sdkPromise;
};

const ensureInitialized = async () => {
	await loadAppleSdk();
	if (!initialized) {
		window.AppleID.auth.init({
			clientId: APPLE_WEB_SERVICE_ID,
			scope: "name email",
			redirectURI: getRedirectUri(),
			usePopup: true
		});
		initialized = true;
	}
};

// Opens the Apple popup and returns the data our server expects:
// { authorizationCode, bundleId, redirectUri }. `bundleId` is the Services ID
// the server uses both as the token-exchange `client_id` and to verify the
// id_token audience.
export const signInWithApple = async () => {
	await ensureInitialized();
	const response = await window.AppleID.auth.signIn();
	return {
		authorizationCode: response?.authorization?.code,
		bundleId: APPLE_WEB_SERVICE_ID,
		redirectUri: getRedirectUri()
	};
};

// The SDK rejects with an object like { error: "popup_closed_by_user" } when
// the user cancels. Lets callers ignore a cancel vs surfacing a real error.
export const isAppleCancel = error =>
	Boolean(
		error &&
			(error.error === "popup_closed_by_user" ||
				error.error === "user_cancelled_authorize" ||
				error.error === "user_trigger_new_signin_flow")
	);
